import React, { Suspense, memo, useEffect, useState } from 'react';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { TecmaSidebar, TecmaSidebarMobile } from '@tecma/ds';
import { useTranslation } from '@tecma/i18n';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import { useSidebarItems } from 'hooks/useNavbarRoutes';
import { useStore } from 'mobx/helpers';

import CrmGoBackButton from 'components/Button.GoBack';
import Header from 'components/Header';
import CrmLoader from 'components/UI.Loader';

import 'styles/pageTemplate.scss';
import { ENVIRONMENT } from 'constants/environment';

type HeaderAddonSetter = (node: React.ReactNode | null) => void;
type TitleOverrideSetter = (node: React.ReactNode | null) => void;
type BodyClassSetter = (className: string | null) => void;
type TitleClassSetter = (className: string | null) => void;

const PageHeaderAddonContext = React.createContext<HeaderAddonSetter | null>(null);
const PageTitleOverrideContext = React.createContext<TitleOverrideSetter | null>(null);
const PageBodyClassContext = React.createContext<BodyClassSetter | null>(null);
const PageTitleClassContext = React.createContext<TitleClassSetter | null>(null);

export const usePageHeaderAddon = (node: React.ReactNode | null) => {
  const setHeaderAddon = React.useContext(PageHeaderAddonContext);

  useEffect(() => {
    if (!setHeaderAddon) return;
    setHeaderAddon(node);

    return () => {
      setHeaderAddon(null);
    };
  }, [node, setHeaderAddon]);
};

export const usePageTitleOverride = (node: React.ReactNode | null) => {
  const setTitleOverride = React.useContext(PageTitleOverrideContext);

  useEffect(() => {
    if (!setTitleOverride) return;
    setTitleOverride(node);

    return () => {
      setTitleOverride(null);
    };
  }, [node, setTitleOverride]);
};

export const usePageBodyClass = (className: string | null) => {
  const setBodyClass = React.useContext(PageBodyClassContext);

  useEffect(() => {
    if (!setBodyClass) return;
    setBodyClass(className);

    return () => {
      setBodyClass(null);
    };
  }, [className, setBodyClass]);
};

export const usePageTitleClass = (className: string | null) => {
  const setTitleClass = React.useContext(PageTitleClassContext);

  useEffect(() => {
    if (!setTitleClass) return;
    setTitleClass(className);

    return () => {
      setTitleClass(null);
    };
  }, [className, setTitleClass]);
};

interface Props {
  breadcrumb?: string;
  BodyComponent: (props) => React.JSX.Element;
  title: string;
  goBack?: boolean;
  setLanguage: () => void;
  headerAddon?: React.ReactNode;
}

const PageTemplate = observer(
  ({ BodyComponent, title = '', goBack = false, setLanguage, headerAddon: headerAddonProp, ...rest }: Props) => {
    const [managedHeaderAddon, setManagedHeaderAddon] = useState<React.ReactNode | null>(null);
    const [managedTitleOverride, setManagedTitleOverride] = useState<React.ReactNode | null>(null);
    const [managedBodyClass, setManagedBodyClass] = useState<string | null>(null);
    const [managedPageTitleClass, setManagedPageTitleClass] = useState<string | null>(null);
    const headerAddon = headerAddonProp ?? managedHeaderAddon;
    const headerAddonSetter: HeaderAddonSetter = setManagedHeaderAddon;
    const titleOverrideSetter: TitleOverrideSetter = setManagedTitleOverride;
    const bodyClassSetter: BodyClassSetter = setManagedBodyClass;
    const titleClassSetter: TitleClassSetter = setManagedPageTitleClass;
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
    const useMobile = useMediaQuery(theme.breakpoints.down(768), { noSsr: true });
    const store = useStore();
    const { openDrawer } = store;
    const { t } = useTranslation();
    const pageName = t(title);
    const titleArray = pageName.split('/');
    const history = useHistory();

    const navigate = (path: string) => {
      history.push(path);
    };

    const items = useSidebarItems(t, navigate, history.location.pathname);

    const defaultTitleContent =
      titleArray.length > 1 ? (
        <>
          <span>{titleArray[0] + ' / '}</span>
          <span>{titleArray[1]}</span>
        </>
      ) : (
        <span>{titleArray[0]}</span>
      );

    const baseWrapperClass =
      typeof openDrawer === 'undefined'
        ? matches === true
          ? 'body-wrapper-close'
          : 'body-wrapper-open'
        : openDrawer === true
          ? 'body-wrapper-open'
          : 'body-wrapper-close';

    const wrapperClassName = [baseWrapperClass, managedBodyClass, headerAddon ? 'has-header-addon' : null].filter(Boolean).join(' ');
    const pageTitleClassName = ['page-title', managedPageTitleClass].filter(Boolean).join(' ');

    return (
      <PageBodyClassContext.Provider value={bodyClassSetter}>
        <PageHeaderAddonContext.Provider value={headerAddonSetter}>
          <PageTitleOverrideContext.Provider value={titleOverrideSetter}>
            <PageTitleClassContext.Provider value={titleClassSetter}>
              <div className='pageTemplate'>
                <Header setLanguage={setLanguage} />
                <div className='pageTemplate-content'>
                  {useMobile ? (
                    <TecmaSidebarMobile items={items} />
                  ) : (
                    <TecmaSidebar
                      items={items}
                      logo={`${ENVIRONMENT.REACT_APP_BUCKET_BASEURL}/initiatives/${store.loginProjectName}/global/img/primary-logo.svg`}
                      logoCollapsed={`${ENVIRONMENT.REACT_APP_BUCKET_BASEURL}/initiatives/${store.loginProjectName}/global/img/logo192.png`}
                    />
                  )}
                  <div className={wrapperClassName}>
                    {headerAddon}
                    <div className={pageTitleClassName} style={{ paddingTop: 55 }}>
                      {goBack && <CrmGoBackButton className='page-back-button' />}
                      {managedTitleOverride ?? defaultTitleContent}
                    </div>
                    <Suspense fallback={<CrmLoader hasBackdrop={false} loading={true} z={true} />}>
                      <BodyComponent title={title} {...rest} />
                    </Suspense>
                  </div>
                </div>
              </div>
            </PageTitleClassContext.Provider>
          </PageTitleOverrideContext.Provider>
        </PageHeaderAddonContext.Provider>
      </PageBodyClassContext.Provider>
    );
  },
);

export default memo(PageTemplate);
